import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { NavigationStart, Router } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  forkJoin,
  from,
  map,
  pairwise,
  skip,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { isEqual } from 'lodash';
import { User } from 'src/app/models/user';
import { PresenceProvider } from 'src/app/services/presence-provider';
import {
  SessionSharedDataService,
  ShareSessionSource,
} from 'src/app/services/session-shared-data.service';
import { InspectModeRepository } from 'src/app/state/inspect-mode-repository.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { UserService } from 'src/app/services/user.service';
import { SpaceRepository, shallowArrayEqual } from 'src/app/state/space.repository';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { Mutex } from 'async-mutex';
import { CallRelatedModalAction, JoinCallDevicesState } from 'src/app/models/device-manger';
import { ChildModeService } from 'src/app/services/child-mode.service';
import { FlagsService, FLAGS } from 'src/app/services/flags.service';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AudioChimes, AudioService } from 'src/app/services/audio.service';
import { DevicesManagerService, CallModalType } from 'src/app/services/devices-manager.service';
import { JoinLeaveNotificationService } from 'src/app/services/join-leave-notification.service';
import {
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationData,
  V2NotificationDataBuilder,
} from 'src/app/services/notification-toaster.service';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { AuthService } from 'src/app/services/auth.service';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { PresenceMonitoringService } from 'src/app/services/presence-monitoring.service';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { RtcServiceController, StopVideoCallReason } from 'src/app/services/rtc.service';
import { SpaceRecordingService } from 'src/app/services/space-recording.service';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import { TranscriptRepository } from 'src/app/state/transcript.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { INFOS } from 'src/app/common/utils/notification-constants';
import { Session } from 'src/app/models/session';
import { ContextTypeEnum } from 'src/app/services/presence_v2.service';
import * as lodash from 'lodash';
import { endCallKey } from 'src/app/sessions/common/y-session';
import { environment } from 'src/environments/environment';
import { UiService } from 'src/app/services/ui.service';
import { SpeedbumpModalComponent } from 'src/app/dialogs/speedbump-modal/speedbump-modal.component';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { ActionsQueryParamsService } from 'src/app/services/actions-query-params.service';
import { AiTutorModeService } from 'src/app/services/ai-tutor-mode.service';
import { PresenceRepository } from 'src/app/state/presence.repository';
import { SessionOverlayModalsService } from 'src/app/services/session-overlay-modals.service';
import { ObserverModeService } from 'src/app/services/observer-mode.service';
import { SpacePermissionsManagerService } from 'src/app/services/space-permissions-manager.service';
import { DeviceType } from 'src/app/common/utils/devices-handle-util';
import { JoinCallPanelComponent } from 'src/app/dialogs/join-call-panel/join-call-panel.component';
import { VideoLayout } from '../../wb-video-call-participant-id/wb-video-call-participant-id.component';
import { JoinCallPanelModalOptions } from '../../services/join-call-modal-opener.service';
import { EventCategory, SessionEvent } from '../../SessionEvent';
import { ShareSessionDialogComponent } from '../../share-session/wb-share-session.component';
import { SwitchCallToThisDeviceComponent } from '../../switch-call-to-this-device/switch-call-to-this-device.component';
import { USERS_PRESENCE_LIST_DEFAULT_LIMIT } from '../wb-top-controls-v2.constants';
import {
  TopControlSize,
  WbTopControlsV2Service,
} from '../../../../services/wb-top-controls-v2.service';

export interface StartCallViewModel {
  canUseProFeature?: boolean;
  isConnecting?: boolean;
  isConnected?: boolean;
  isHost?: boolean;
  isCallActive: boolean;
  clickDisabled?: boolean;
  hovered?: boolean;
  focused?: boolean;
  inCallUserSet: Set<string>;
  hasEncounteredCallError?: boolean;
  usersListInSpaceNotInCall?: User[];
  isFreeSpace?: boolean;
  isStartCallEnabled?: boolean;
  observerModeActive?: boolean;
}

export const USER_AVATAR_SIZE = 28;
export const USER_AVATAR_OFFSET = 20;
export const USER_ON_CALL_AVATAR_SIZE = 26;
export const USER_ON_CALL_AVATAR_OFFSET = 20;
export const USER_OBSERVER_AVATAR_SIZE = 26;
export const USER_OBSERVER_AVATAR_OFFSET = 20;

/** Emitted when the join-call precall dialog should open; session layer opens via JoinCallModalOpenerService (session injector). */
export interface ShowJoinCallModalPayload {
  joinCallParams: MatDialogConfig<Record<string, unknown>>;
  modalOptions: JoinCallPanelModalOptions<{
    action: CallRelatedModalAction;
    joinCallDevicesState: JoinCallDevicesState;
  }>;
}

/** Devices to join with when the precall modal is skipped. AI Tutor never reaches this: it
 * always joins without the modal (see CallAndPresencesService.joinAiTutorCall). */
export function getAutomaticJoinCallDevicesState(
  skipPrecallSetup: boolean,
  observerMode: boolean,
): JoinCallDevicesState | undefined {
  if (skipPrecallSetup || observerMode) {
    return { unmuteCam: false, unmuteMic: false };
  }
  return undefined;
}

export const calculatePresenceListContainerWidth = (
  usersCount: number,
  limit: number,
  width: number,
  offset: number,
  extraPadding = 0,
): number => {
  if (usersCount === 0) {
    return 0;
  }

  const avatarsCount = usersCount > limit ? limit + 1 : usersCount;
  return avatarsCount * width - (avatarsCount - 1) * (width - offset) + extraPadding;
};

/**
 * this service hold logic for presence and join call
 * for presence, the service handle br change, notify others by the current user presence state
 * for join call, the service handle all logic for starting a call, opening joining model
 * + this service holds shared state for call and presence group component
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CallAndPresencesService {
  private handleRoomChangeMutex = new Mutex();
  isJoinCallPendingOnRoomChange = false;
  newRoomJoinCallDevicesState: JoinCallDevicesState = {
    unmuteCam: false,
    unmuteMic: false,
  };

  private isCallEnded = false;

  joinCallFromAnotherDeviceTooltipOpened = new BehaviorSubject(false);
  joinCallFromAnotherDeviceTooltipOpened$ = this.joinCallFromAnotherDeviceTooltipOpened
    .asObservable()
    .pipe(distinctUntilChanged());

  private isJoinDialogOpen = new BehaviorSubject(false);
  public isJoinDialogOpen$ = this.isJoinDialogOpen.pipe(distinctUntilChanged());
  private callLaunchPending = false;

  private readonly showJoinCallModalSubject = new Subject<ShowJoinCallModalPayload>();
  readonly showJoinCallModal$ = this.showJoinCallModalSubject.asObservable();

  isChimesEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_USER_CHIMES);
  inCallUsersCount = 0;
  allowJoinCallModal = true;

  viewModel: StartCallViewModel = {
    isCallActive: false,
    inCallUserSet: new Set<string>(),
    hasEncounteredCallError: false,
  };

  constructor(
    private spaceRepo: SpaceRepository,
    private userService: UserService,
    private telemetry: TelemetryService,
    private modalManagerService: ModalManagerService,
    private presenceProvider: PresenceProvider,
    private rtcServiceController: RtcServiceController,
    private spaceRecordingService: SpaceRecordingService,
    private devicesManagerService: DevicesManagerService,
    private wbDialogService: WbDialogService,
    private joinLeaveNotificationService: JoinLeaveNotificationService,
    private sessionSharedDataService: SessionSharedDataService,
    private presenceMonitoringService: PresenceMonitoringService,
    private flagsService: FlagsService,
    private transcriptRepo: TranscriptRepository,
    private audioService: AudioService,
    private translate: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private dialog: MatDialog,
    private realtimeSpaceService: RealtimeSpaceService,
    private uiService: UiService,
    private providerState: ProviderStateService,
    private localTrackManagerService: LocalTracksManagerService,
    private domListenerFactoryService: DomListenerFactoryService,
    private topControlService: WbTopControlsV2Service,
    private authService: AuthService,
    private featureManagementService: FeatureManagementService,
    private router: Router,
    private presenceRepository: PresenceRepository,
    private sessionOverlayModalsService: SessionOverlayModalsService,
    private inspectModeRepository: InspectModeRepository,
    private actionsQueryParamsService: ActionsQueryParamsService,
    private aiTutorModeService: AiTutorModeService,
    private childModeService: ChildModeService,
    private observerModeService: ObserverModeService,
    private spacePermissionsManagerService: SpacePermissionsManagerService,
  ) {
    this.initialize();
  }

  /** Whether Pencil call UI and call-related prompts should be shown in the current Space session. */
  areCallFeaturesAvailable(): boolean {
    return !this.actionsQueryParamsService.checkIfWhiteboardOnlyMode();
  }

  private domListener = this.domListenerFactoryService.createInstance();

  usersListInSpaceNotInCall$: Observable<User[]> = this.spaceRepo.spaceIdAndRoomId$.pipe(
    switchMap(([spaceId, roomId]) =>
      combineLatest([
        this.presenceProvider.getCallPresenceActivity(spaceId, roomId),
        this.presenceProvider.getRoomPresenceActivity(spaceId, roomId),
      ]),
    ),
    tap(() => {
      this.joinLeaveNotificationService.setUserLookUpArray(this.usersLookup);
    }),
    map(([callActivity, presenceActivity]) =>
      this.populatedUsers.filter(
        (user) => !callActivity.has(user._id) && presenceActivity.has(user._id),
      ),
    ),
  );

  usersListInCall$: Observable<User[]> = this.presenceProvider.inCallUserSet$.pipe(
    map((callActivity) => this.populatedUsers.filter((user) => callActivity.has(user._id))),
  );

  usersListObservers$: Observable<User[]> = this.spaceRepo.spaceIdAndRoomId$.pipe(
    distinctUntilChanged((prev, cur) => shallowArrayEqual(prev, cur)),
    switchMap(([spaceId, roomId]) =>
      this.presenceProvider.getSpacePresenceObserversIDs$(spaceId, roomId),
    ),
    map((observerIds) => {
      const canViewObservers = this.spaceRepo.hasSpaceAdminPermissions(
        this.userService.user.getValue()?.user,
        this.spaceRepo.activeSpace,
        true,
      );

      if (!canViewObservers) {
        return [];
      }

      return this.populatedUsers.filter((user) => observerIds.has(user._id));
    }),
  );

  isCurrentUserInCall$ = this.spaceRepo.spaceIdAndRoomId$.pipe(
    switchMap(([spaceId, roomId]) =>
      combineLatest([
        this.presenceProvider.getCallPresenceActivity(spaceId, roomId),
        this.userService.user.asObservable(),
      ]),
    ),
    map(([callActivity, user]) => callActivity.has(user?.user._id as string)),
  );

  readonly shouldShowCallNotReadyNotification$ = this.presenceRepository.gotPresenceUpdate.pipe(
    take(1),
    switchMap(() =>
      combineLatest([
        this.presenceProvider.isCallActive$,
        this.isJoinDialogOpen$,
        this.spaceRepo.isCurrentUserHost$,
        this.providerState.callConnected$,
        this.presenceProvider.anyHostActiveInCurrentRoom$,
        this.sessionOverlayModalsService.gvCallNotConnectedOverlayModalShown$,
      ]).pipe(
        map(
          ([
            isCallActive,
            isJoinDialogOpen,
            isHost,
            isConnected,
            isHostInSpace,
            isGVCallNotConnectedOverlayModalShown,
          ]) => ({
            shouldShowNotification:
              this.areCallFeaturesAvailable() &&
              !this.actionsQueryParamsService.checkIfShouldSkipCallHostCheck() &&
              !isHost &&
              !isCallActive &&
              !isJoinDialogOpen &&
              !isConnected &&
              !isGVCallNotConnectedOverlayModalShown,
            isHostInSpace,
          }),
        ),
      ),
    ),
  );

  readonly rtcConnectionState$ = combineLatest([
    this.providerState.callConnecting$.pipe(distinctUntilChanged()),
    this.providerState.callConnected$.pipe(
      distinctUntilChanged(),
      tap((isConnected) => {
        this.handleBeforeUnload(isConnected);

        if (isConnected) {
          this.sessionSharedDataService.startVideoAnimation.next();
        }
      }),
    ),
    this.providerState.callDisconnecting$.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([isConnecting, isConnected, isDisconnecting]) => ({
      isConnecting,
      isConnected,
      isDisconnecting,
    })),
  );

  readonly startCallButtonVm$: Observable<StartCallViewModel> = combineLatest([
    this.featureManagementService.currentSpaceBelongToFreeGuestUser$,
    this.featureManagementService.isStartCallEnabled$,
    this.rtcConnectionState$,
    this.spaceRepo.isCurrentUserHost$,
    this.usersListInCall$,
    this.presenceProvider.inCallUserSet$,
    this.isJoinDialogOpen$,
    this.topControlService.topControlSize$,
    this.usersListInSpaceNotInCall$,
    this.observerModeService.observerModeActive$,
  ]).pipe(
    map(
      ([
        isFreeSpace,
        isStartCallEnabled,
        rtcConnectionState,
        isHost,
        usersInCall,
        inCallUserSet,
        isJoinDialogOpen,
        topControlSize,
        usersListInSpaceNotInCall,
        observerModeActive,
      ]) => {
        const isCallActive = inCallUserSet.size > 0;
        const canRenderPresenceList = !rtcConnectionState.isConnecting && isCallActive;

        return {
          isFreeSpace, // New field for determining free guest space
          isStartCallEnabled, // Handles tier-level start call capability
          ...rtcConnectionState,
          isHost,
          clickDisabled: !isStartCallEnabled, // handles for free space / tiers without call capability
          usersInCall,
          isCallActive,
          inCallUserSet,
          focused: isJoinDialogOpen,
          avatarWidth: USER_ON_CALL_AVATAR_SIZE,
          avatarOffset: USER_ON_CALL_AVATAR_OFFSET,
          canEndCallForEveryone: isHost && inCallUserSet.size > 1,
          canRenderPresenceList,
          usersPresenceListConfig: this.getUserPresenceListConfig({
            canRenderPresenceList,
            topControlSize,
            usersInCall,
          }),
          topControlSize,
          usersListInSpaceNotInCall,
          observerModeActive,
        };
      },
    ),
    distinctUntilChanged(isEqual),
  );

  private getUserPresenceListConfig(config: {
    canRenderPresenceList: boolean;
    topControlSize: TopControlSize;
    usersInCall: User[];
  }) {
    if (!config.canRenderPresenceList) {
      return null;
    }

    const width =
      config.topControlSize === TopControlSize.XSmall
        ? USER_ON_CALL_AVATAR_SIZE
        : calculatePresenceListContainerWidth(
            config.usersInCall.length,
            USERS_PRESENCE_LIST_DEFAULT_LIMIT,
            USER_ON_CALL_AVATAR_SIZE,
            USER_ON_CALL_AVATAR_OFFSET,
          );

    return {
      users: config.usersInCall,
      limit: USERS_PRESENCE_LIST_DEFAULT_LIMIT,
      showCounterOnly: config.topControlSize === TopControlSize.XSmall,
      avatarWidth: USER_ON_CALL_AVATAR_SIZE,
      avatarOffset: USER_ON_CALL_AVATAR_OFFSET,
      width,
    };
  }

  private initialize(): void {
    this.startCallButtonVm$.pipe(untilDestroyed(this)).subscribe((vm) => {
      this.viewModel = vm;
    });

    this.setupCallAndPresenceListners();

    this.setupSessionMetadataObservable();
    this.logSpaceLoadingDataToFullStory();

    this.devicesManagerService.hostEndedCallBeforeAnyoneJoined
      .pipe(
        untilDestroyed(this),
        filter((hostEndedCallBeforeAnyoneJoined) => hostEndedCallBeforeAnyoneJoined),
      )
      .subscribe(() => {
        this.allowJoinCallModal = true;
      });

    // When child mode becomes active for the current user (demotion from host, or child mode turned on for existing participant),
    // re-show the join call modal so they are prompted to join if a call is active and they are not in it.
    this.childModeService.isChildModeActive$
      .pipe(
        untilDestroyed(this),
        pairwise(),
        filter(([wasActive, isActive]) => !wasActive && isActive),
      )
      .subscribe(() => this.allowAndShowJoinCallModalIfEligible());
  }

  private async handleSpaceOrBreakoutRoomChange(
    currentSpaceId: string,
    newSpaceId: string,
    currentBreakoutRoomId?: string,
    newBreakoutRoomId?: string,
    staleJoinCallDialogRef?: MatDialogRef<JoinCallPanelComponent>,
  ) {
    // Close the join call panel that belonged to the room we're leaving (captured before the RTC
    // teardown awaits)
    staleJoinCallDialogRef?.close();
    this.joinLeaveNotificationService.setUserLookUpArray(this.usersLookup);
    if (currentBreakoutRoomId && newBreakoutRoomId && currentBreakoutRoomId !== newBreakoutRoomId) {
      await this.handlePendingJoinCallOnRoomChange();
    }
  }

  private async handlePendingJoinCallOnRoomChange() {
    let joinedCall = false;
    // Automatic join call if the user was connected to a call before room changing
    if (this.isJoinCallPendingOnRoomChange) {
      joinedCall = await this.devicesManagerService.joinCallWithDeviceState(
        this.newRoomJoinCallDevicesState,
        false,
        true,
        true,
        true,
      );

      this.isJoinCallPendingOnRoomChange = false;
    }

    // Build title message
    const titleMessage = joinedCall
      ? `${this.translate.instant('You joined the call in')} ${
          this.spaceRepo.activeSpaceCurrentRoom?.name
        }`
      : `${this.translate.instant('You are now in')} ${
          this.spaceRepo.activeSpaceCurrentRoom &&
          this.spaceRepo.activeSpaceId &&
          Session.isMainRoom(
            this.spaceRepo.activeSpaceCurrentRoom.uid,
            this.spaceRepo.activeSpaceId,
            true,
          )
            ? this.translate.instant('the')
            : ''
        } ${this.spaceRepo.activeSpaceCurrentRoom?.name}`;

    // Use the standardized info notification method (same API as showDefaultErrorNotificationV2)
    this.notificationToasterService.showDefaultInfoNotificationV2(
      titleMessage, // title
      '', // description (empty for this notification)
      INFOS.JOIN_CALL_ON_ROOM_CHANGE, // notification code
      {
        assetName: 'breakout_rooms_transparent_grey',
        type: NotificationIconTypes.SVG_ICON,
        width: 15,
        height: 15,
      },
      undefined,
      10,
    );
  }

  logSpaceLoadingDataToFullStory(): void {
    /*
      take(2): take the first two values, skip(1): skip the first value which is an empty set
      so the combination of take(2), skip(1) will take the first non-empty presence set
    */
    combineLatest([
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
      this.spaceRepo.activeSpaceCurrentRoomUid$,
    ])
      .pipe(
        switchMap(([spaceId, roomId]) =>
          forkJoin([
            this.presenceProvider.getRoomPresenceActivity(spaceId, roomId).pipe(take(2), skip(1)),
            this.spaceRepo.hasActiveSpaceSynced$.pipe(
              filter((isSynced) => isSynced),
              first(),
            ),
          ]),
        ),
      )
      .pipe(untilDestroyed(this))
      .subscribe(([presence]) => {
        const active = presence.size;
        let hosts = 0;
        let participants = 0;
        let invited = 0;
        const activeSpace = this.spaceRepo.activeSpace;
        if (activeSpace) {
          activeSpace.users?.forEach((user) => {
            if (user.joined) {
              Session.isOwnedByUser(activeSpace, user._id) ? hosts++ : participants++;
            } else {
              invited++;
            }
          });
        }
        this.telemetry.event('spaces_did_load', {
          n_users_active: active,
          n_users_hosts: hosts,
          n_users_participants: participants,
          n_users_invited: invited,
        });
      });
  }

  setupCallAndPresenceListners(): void {
    this.devicesManagerService.isCallActive = false;

    combineLatest([
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
      this.spaceRepo.activeSpaceCurrentRoomUid$.pipe(startWith(undefined), distinctUntilChanged()),
    ])
      .pipe(
        pairwise(),
        switchMap(([[prevSpaceId, prevRoomId], [spaceId, roomId]]) =>
          this.handleRoomChangeThenGetCallActivity$(prevSpaceId, spaceId, prevRoomId, roomId),
        ),
        untilDestroyed(this),
      )
      .subscribe(([callActivity, presence]) => {
        this.setupCallListeners(callActivity, presence);
        this.setupPresenceListeners(callActivity, presence);
      });

    this.spaceRepo.spaceIdAndRoomId$
      .pipe(
        switchMap(() => this.providerState.callConnected$.pipe(distinctUntilChanged(), pairwise())),
      )
      .pipe(untilDestroyed(this))
      .subscribe(([prevCallConnected, callConnected]) => {
        if (prevCallConnected && !callConnected && this.viewModel.isCallActive) {
          this.allowJoinCallModal = false;
        }
      });
  }

  /**
   * Runs the mutex-guarded room-change side effects (RTC teardown + closing the stale join-call
   * modal), then fetches call/presence activity for the destination room. Fetching only starts once
   * the mutex task resolves, so this can't open a new join-call modal before the stale one is closed.
   */
  private handleRoomChangeThenGetCallActivity$(
    prevSpaceId: string,
    spaceId: string,
    prevRoomId: string | undefined,
    roomId: string | undefined,
  ): Observable<[Set<string>, Set<string>]> {
    // Reset showing the join call modal in case of switching between spaces/rooms without reloading
    this.allowJoinCallModal = true;

    // Captured synchronously, before any awaits below
    const staleJoinCallDialogRef = this.wbDialogService.joinCallDialogRef;

    return from(
      this.handleRoomChangeMutex.runExclusive(async () => {
        if (prevRoomId !== roomId) {
          await this.handleBreakoutRoomChangedRTC(roomId);
        }

        if (this.spaceRepo.activeSpaceId !== prevSpaceId || prevRoomId !== roomId) {
          await this.handleSpaceOrBreakoutRoomChange(
            prevSpaceId,
            spaceId,
            prevRoomId,
            roomId,
            staleJoinCallDialogRef,
          );
        }
      }),
    ).pipe(
      switchMap(() =>
        roomId
          ? this.spaceRepo.hasActiveSpaceSynced$.pipe(
              filter(Boolean),
              switchMap(() =>
                combineLatest([
                  this.presenceProvider.getCallPresenceActivity(spaceId, roomId),
                  this.presenceProvider.getRoomPresenceActivity(spaceId, roomId),
                ]),
              ),
            )
          : EMPTY,
      ),
    );
  }

  setupCallListeners(callActivity: Set<string>, presence: Set<string>): void {
    // To cover any unexpected scenario if removing a user isn't synced with call activity
    const activeSpace = this.spaceRepo.getSpace(this.spaceRepo.activeSpaceId as string);
    let isUserRemoved = false;
    if (this.user) {
      isUserRemoved = !activeSpace?.users.find((user) => user._id === this.user?._id);
    }

    if (!callActivity || isUserRemoved) {
      return;
    }

    const { inCallUsers, outCallUsers } = this.getInOutCallUsers(presence, callActivity);

    const isCallActive = callActivity.size > 0;
    this.devicesManagerService.isCallActive = isCallActive;
    this.presenceMonitoringService.notifyPresenceActivityChange(
      this.spaceRepo.activeSpaceId as string,
      this.spaceRepo.activeSpaceCurrentRoomUid as string,
      callActivity,
      inCallUsers,
      outCallUsers,
      ContextTypeEnum.CALL,
    );

    if (
      isCallActive &&
      this.isChimesEnabled &&
      callActivity.size > 1 &&
      callActivity.size <= 4 &&
      this.inCallUsersCount < inCallUsers.length
    ) {
      this.debouncedUserJoinsCallSoundPlay();
    }
    this.inCallUsersCount = inCallUsers.length;
    if (!isCallActive) {
      this.allowJoinCallModal = true;
    }
    this.checkAndShowJoinCallModal(false);
    this.transcriptRepo.updateSpaceCallConnected(isCallActive);

    if (!this.shouldEjectTheUserFromAnotherDevice()) {
      this.closeJoinCallFromSameAccountPopupIfExist();
    }
  }

  private debouncedUserJoinsCallSoundPlay = lodash.debounce(
    () => {
      this.audioService.playAudio(AudioChimes.userJoinsCall);
    },
    2000,
    { leading: true, trailing: false },
  );

  setupPresenceListeners(callActivity: Set<string>, presence: Set<string>): void {
    const { inCallUsers, outCallUsers } = this.getInOutCallUsers(presence, callActivity);

    this.presenceMonitoringService.notifyPresenceActivityChange(
      this.spaceRepo.activeSpaceId as string,
      this.spaceRepo.activeSpaceCurrentRoomUid,
      presence,
      inCallUsers,
      outCallUsers,
      ContextTypeEnum.SPACE,
    );
  }

  private getInOutCallUsers(
    presence: Set<string>,
    inCallActivity: Set<string>,
  ): { inCallUsers: User[]; outCallUsers: User[] } {
    const inCallUsers: User[] = [];
    const outCallUsers: User[] = [];
    Object.entries(this.usersLookup).forEach(([userId, user]) => {
      inCallActivity.has(userId) ? inCallUsers.push(user) : outCallUsers.push(user);
    });

    return { inCallUsers, outCallUsers };
  }

  private setupSessionMetadataObservable() {
    this.spaceRepo.activeSpaceId$
      .pipe(
        untilDestroyed(this),
        filterNil(),
        distinctUntilChanged(),
        switchMap((spaceId) => this.spaceRepo.spaceMetadata$(spaceId)),
        filterNil(),
      )
      .subscribe((metadata) => {
        if (
          this.isCallEnded !==
          metadata.sessionMetadata[endCallKey(this.spaceRepo.activeSpaceSelectedBoardUid)]
        ) {
          this.isCallEnded =
            metadata.sessionMetadata[endCallKey(this.spaceRepo.activeSpaceSelectedBoardUid)];

          // To cover the race condition between Firebase call presence reconnection & session metadata sync
          if (this.checkAndShowJoinCallModal(false)) {
            // log to FullStory
            try {
              this.telemetry.event('Show join call modal after session metadata synced', {});
            } catch (e) {
              console.log('fullstory event failed', e);
            }
            // log to console for non-prod builds
            if (!environment.production) {
              console.log('Show join call modal after session metadata synced');
            }
          }
        }
      });
  }

  private checkAndShowJoinCallModal(isStartCall: boolean): boolean {
    if (this.inspectModeRepository.isInspectMode) {
      return false;
    }
    if (
      this.allowJoinCallModal &&
      this.viewModel.isCallActive &&
      !this.viewModel.isConnected &&
      !this.viewModel.isConnecting &&
      !this.isCallEnded &&
      !this.viewModel.hasEncounteredCallError &&
      !this.isJoinCallPendingOnRoomChange &&
      !this.shouldEjectTheUserFromAnotherDevice()
    ) {
      this.showJoinCallModal(isStartCall);
      return true;
    }
    return false;
  }

  /**
   * Re-allow and show the join call modal if conditions are met.
   * Used by ChildModeService when a host is demoted to participant (or child mode is activated for a participant) so they are prompted to join the call.
   */
  allowAndShowJoinCallModalIfEligible(): void {
    this.allowJoinCallModal = true;
    this.checkAndShowJoinCallModal(false);
  }

  async showJoinCallModal(isStartCall: boolean): Promise<void> {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    if (this.sessionSharedDataService.controlsLayout.value === VideoLayout.POPOUT) {
      return;
    }
    this.allowJoinCallModal = false;

    const joinRoomModal = this.dialog.openDialogs.find(
      (d) => d.componentInstance instanceof SpeedbumpModalComponent,
    );

    if (joinRoomModal) {
      await firstValueFrom(joinRoomModal.afterClosed());
    }

    if (this.aiTutorModeService.enabled) {
      await this.joinAiTutorCall();
      return;
    }

    const automaticDevicesState = getAutomaticJoinCallDevicesState(
      this.actionsQueryParamsService.checkIfShouldSkipPrecallSetup(),
      this.observerModeService.isObserverModeActive(),
    );
    if (automaticDevicesState) {
      await this.devicesManagerService.joinCallWithDeviceState(automaticDevicesState);
      return;
    }

    const data = {
      isStartCall: isStartCall,
      usersLookUp: this.usersLookup,
      sessionId: this.spaceRepo.activeSpaceId,
      breakoutRoomId: this.spaceRepo.activeSpaceCurrentRoomUid,
      audioOnly: this.aiTutorModeService.enabled,
    };

    const modalOptions: JoinCallPanelModalOptions<{
      action: CallRelatedModalAction;
      joinCallDevicesState: JoinCallDevicesState;
    }> = {
      afterClosed: (res) => {
        this.isJoinDialogOpen.next(false);

        this.localTrackManagerService.closeUnneededStreamsOnCallModalClose(
          res.action,
          res.joinCallDevicesState,
        );
        switch (res.action) {
          case CallRelatedModalAction.START_JOINING_THE_CALL:
            this.devicesManagerService.joinCallWithDeviceState(res.joinCallDevicesState);
            break;
          case CallRelatedModalAction.OPEN_SPACE_DEVICE_MODAL:
            this.devicesManagerService.openDeviceSelection({
              returnBackToJoinCallModal: true,
            });
            break;
          case CallRelatedModalAction.OPEN_VIRTUAL_BACKGROUND_MODAL:
            this.devicesManagerService.openVirtualBackgroundModal(CallModalType.JOIN_CALL_MODAL);
            break;
          case CallRelatedModalAction.NOT_PROCEEDING_WITH_THE_CALL:
            if (isStartCall) {
              this.allowJoinCallModal = true;
            }
            break;
        }
      },
    };
    const joinCallParams = {
      panelClass: 'open-call-dialog',
      autoFocus: false,
      data: data,
      disableClose: true,
      closeOnNavigation: true,
    };

    this.devicesManagerService.logJoinCallModalShownToFullStory();
    this.isJoinDialogOpen.next(true);

    this.showJoinCallModalSubject.next({ joinCallParams, modalOptions });

    if (!isStartCall) {
      this.audioService.playAudio(AudioChimes.userStartsCall, 0.5);
    }
  }

  /**
   * AI Tutor joins the call straight away and never shows the join modal, so a student can
   * never be left outside the call by a permission prompt or by closing a dialog.
   *
   *  - Microphone already granted and working: join with it on, as before.
   *  - Anything else (prompt, denied, dismissed, no device, Permissions API missing): join
   *    MUTED, with no tracks, so the chat works at once. Then ask for the microphone once in
   *    the background ({@link requestAiTutorMicrophoneAfterJoin}).
   *
   * A missing microphone shows as the mic button's amber error state, which is also where
   * the student fixes it: clicking it opens the permission helper.
   */
  private async joinAiTutorCall(): Promise<void> {
    const micReady = await this.ensureAiTutorMicrophoneReady();
    const joined = await this.devicesManagerService.joinCallWithDeviceState({
      unmuteCam: false,
      unmuteMic: micReady,
    });
    if (joined && !micReady) {
      void this.requestAiTutorMicrophoneAfterJoin();
    }
  }

  /**
   * Ask for the microphone once after an AI Tutor student joined muted. Never blocks the
   * join, which has already happened.
   *
   * On "prompt" this shows the browser's permission prompt; granting it turns the mic on, so
   * the student can talk without another click. Denying or dismissing it, or having no
   * microphone, makes getUserMedia fail, which flags the mic device state: the mic button
   * turns amber with the matching tooltip ("Microphone permissions not granted", "No
   * microphone available"), and the student keeps chatting.
   *
   * Calls `acquireAudioStream` rather than `unmute`: unmute starts the mic loading state and
   * its timeout, which an unanswered prompt would trip.
   */
  private async requestAiTutorMicrophoneAfterJoin(): Promise<void> {
    try {
      await this.localTrackManagerService.acquireAudioStream();
    } catch (_) {
      // The failure is already on the mic device state (the amber mic button).
      return;
    }
    if (
      this.localTrackManagerService.isAudioTrackAcquired() &&
      this.providerState.callConnectedValue &&
      !this.rtcServiceController.service.isMicEnabled() &&
      this.spacePermissionsManagerService.canOpenMic()
    ) {
      await this.localTrackManagerService.unmute(DeviceType.AUDIO);
    }
  }

  /**
   * Acquire an already-authorized microphone before AI Tutor joins, so a granted student
   * joins with the mic on. Unknown, prompt, denied, or failed states return false and the
   * student joins muted instead (see {@link joinAiTutorCall}).
   */
  private async ensureAiTutorMicrophoneReady(): Promise<boolean> {
    if (this.localTrackManagerService.isAudioTrackAcquired()) {
      return true;
    }

    try {
      if (!navigator.permissions?.query) {
        return false;
      }
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      if (permissionStatus.state !== 'granted') {
        return false;
      }

      await this.localTrackManagerService.acquireAudioStream();
      return this.localTrackManagerService.isAudioTrackAcquired();
    } catch (_) {
      return false;
    }
  }

  /** Shared guarded launcher for toolbar, scheduled and AI-tutor entry points. */
  startOrJoinCall(): boolean {
    if (
      this.inspectModeRepository.isInspectMode ||
      this.observerModeService.isObserverModeActive() ||
      this.viewModel.isConnected ||
      this.viewModel.isConnecting ||
      this.isJoinDialogOpen.value ||
      this.callLaunchPending ||
      !this.featureManagementService.isStartCallEnabled()
    ) {
      return false;
    }
    // AI Tutor hides the top controls, where the desktop "switch call to this device" prompt
    // lives, so asking would leave the student outside the call and Luna never invited.
    // Take the call over on this device instead, as accepting the prompt would.
    if (this.shouldEjectTheUserFromAnotherDevice() && !this.aiTutorModeService.enabled) {
      this.showEjectOtherDeviceAndJoinCallConfirmation();
      return false;
    }

    this.allowJoinCallModal = false;
    this.callLaunchPending = true;
    const finishLaunch = () => (this.callLaunchPending = false);
    void this.showJoinCallModal(!this.viewModel.isCallActive).then(finishLaunch, finishLaunch);
    return true;
  }

  private async handleBreakoutRoomChangedRTC(breakoutRoomId: string | undefined) {
    if (breakoutRoomId) {
      // in case the call is connecting wait until the call being connected/disconnected
      // to prevent switching rooms while joining the old room call
      await firstValueFrom(
        this.providerState.callConnecting$.pipe(filter((value) => value === false)),
      );
      if (this.rtcServiceController.service.isConnected()) {
        this.isJoinCallPendingOnRoomChange = true;
        this.preserveDevicesStateOnRoomChange();

        await this.rtcServiceController.stopVideoCall(
          this.spaceRecordingService,
          StopVideoCallReason.LEAVE_CALL_PROGRAMMATICALLY,
          {
            isBreakoutRoomChange: true,
          },
        );
      }
    }
  }

  private preserveDevicesStateOnRoomChange() {
    this.newRoomJoinCallDevicesState.unmuteCam =
      this.rtcServiceController.service.isCameraEnabled();
    this.newRoomJoinCallDevicesState.unmuteMic = this.rtcServiceController.service.isMicEnabled();
  }

  private checkIfHost(userOrUserId: User | string): boolean {
    const userId = typeof userOrUserId === 'string' ? userOrUserId : userOrUserId?._id;
    const session = this.spaceRepo.activeSpace;
    if (!session) {
      return false;
    }

    const event = session.event;

    if (event) {
      if (event.host_ids?.includes(userId)) {
        return true;
      }
      return false;
    }

    // these values don't update in the space yet, so we wait for them
    if (!session.users || !session.owner) {
      return true;
    }
    return Session.isOwnedByUser(session, userId);
  }

  endCallForAll(): void {
    const event = new SessionEvent(EventCategory.CallManager, 'EndCall', {
      sessionId: this.spaceRepo.activeSpaceId as string,
      breakoutRoomId: this.spaceRepo.activeSpaceCurrentRoomUid as string,
    });
    this.realtimeSpaceService.service.sendEvent(this, event);
    this.realtimeSpaceService.service.modifySessionMetadata({
      [endCallKey(this.spaceRepo.activeSpaceCurrentRoomUid)]: true,
    });

    // [discard] This is aleady called in sessionEventReceived on endCall event -->
    this.stopRtc(StopVideoCallReason.END_CALL_FOR_EVERYONE);
  }

  closeJoinCallFromSameAccountPopupIfExist() {
    this.wbDialogService.getDialogRefOf(SwitchCallToThisDeviceComponent)?.close();
    this.joinCallFromAnotherDeviceTooltipOpened.next(false);
  }

  shouldEjectTheUserFromAnotherDevice(): boolean {
    return (
      this.viewModel.inCallUserSet.has(this.user?._id as string) &&
      !this.flagsService.isFlagEnabled(FLAGS.ENABLE_MULTIPLE_JOIN_CALL_FROM_THE_SAME_ACCOUNT)
    );
  }

  // eject other devices logic will not work well with observer mode so we prevent showing it for observers
  showEjectOtherDeviceAndJoinCallConfirmation() {
    if (this.observerModeService.isObserverModeActive()) {
      return;
    }

    if (this.uiService.isMobile.value) {
      const joinCallParams = {
        panelClass: 'join-call-from-another-device-dialog',
        autoFocus: false,
        disableClose: true,
        closeOnNavigation: true,
      };

      const modalOptions = {
        afterClosed: (res: { isJoinCall: boolean }) => {
          if (res && res.isJoinCall) {
            this.joinCurrentCallFromSameAccount();
          }
        },
      };

      this.modalManagerService.showModal(
        SwitchCallToThisDeviceComponent,
        joinCallParams,
        modalOptions,
      );
    } else {
      if (!this.joinCallFromAnotherDeviceTooltipOpened.value) {
        this.joinCallFromAnotherDeviceTooltipOpened.next(true);
      }
    }
  }

  joinCurrentCallFromSameAccount() {
    this.showJoinCallModal(false);
    this.closeJoinCallFromSameAccountPopupIfExist();
  }

  stopRtc(reason: StopVideoCallReason): void {
    this.rtcServiceController.stopVideoCall(this.spaceRecordingService, reason);
  }

  shareSession(): void {
    try {
      this.telemetry.event('[Spaces referral] open invite modal from the top bar', {});
    } catch (e) {
      console.log('fullstory event failed', e);
    }
    const shareDialogParams = {
      panelClass: 'share-wb-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        source: ShareSessionSource.TOP_CONTROLS,
      },
    };

    this.modalManagerService.showModal(ShareSessionDialogComponent, shareDialogParams);
  }

  private appNavigationSubscription?: Subscription;
  private handleBeforeUnload(isConnected: boolean): void {
    this.appNavigationSubscription?.unsubscribe();
    this.domListener.remove(window, 'beforeunload');

    if (!isConnected) {
      return;
    }

    this.appNavigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Check if back navigation is triggered
        if (event.navigationTrigger === 'popstate' && event.url !== this.router.url) {
          if (
            this.rtcServiceController.service.isConnected() &&
            !this.userService.isUpdateButtonClicked.getValue() &&
            Boolean(this.authService.fbUser.getValue())
          ) {
            if (!confirm('Are you sure you want to leave?')) {
              this.router.navigateByUrl(this.router.url);
            }
          }
        }
      }
    });

    this.domListener.add(window, 'beforeunload', (event) => {
      if (
        this.rtcServiceController.service.isConnected() &&
        !this.userService.isUpdateButtonClicked.getValue() &&
        Boolean(this.authService.fbUser.getValue())
      ) {
        event.preventDefault();
        event.returnValue = 'Are you sure you want to leave?';
      }
    });
  }

  private notificationData: V2NotificationData | null = null;
  public showCallNotReadyNotification(hostPresentInSpace: boolean) {
    const title = hostPresentInSpace
      ? this.translate.instant('Waiting for host to start the call')
      : this.translate.instant('Host is not here');
    const description = this.translate.instant('You can join the call after they start it.');
    const icon = hostPresentInSpace ? 'call' : 'person';

    if (this.notificationData && this.notificationData.icon?.assetName === icon) {
      return;
    }

    if (!this.notificationData) {
      this.notificationData = new V2NotificationDataBuilder(INFOS.PARTICIPANT_IN_SPACE_NOT_IN_CALL)
        .type(NotificationType.INFO)
        .style(ToasterPopupStyle.INFO)
        .title(title)
        .description(description)
        .icon({
          assetName: icon,
          size: 18,
          type: NotificationIconTypes.MAT_ICON,
          outlined: false,
        })
        .showProgressBar(false)
        .dismissable(true)
        .build();
      this.notificationToasterService.showNotification(this.notificationData, () => {
        this.notificationData = null;
      });
    } else {
      this.notificationData.title = title;
      (this.notificationData.icon as NotificationIconConfig).assetName = icon;
    }
  }

  public dismissParticipantWaitingForCallNotification() {
    this.notificationToasterService.dismissNotificationsByCode([
      INFOS.PARTICIPANT_IN_SPACE_NOT_IN_CALL,
    ]);
  }

  get isHost(): boolean {
    if (!this.user) {
      return false;
    }
    return this.checkIfHost(this.user);
  }

  get user(): User | undefined {
    return this.userService.user.getValue()?.user;
  }

  get usersLookup(): {
    [key: string]: User;
  } {
    return (this.spaceRepo.activeSpace?.populatedUsers || []).reduce(
      (prev, curr) => ({ ...prev, [curr._id]: curr }),
      {},
    );
  }

  get populatedUsers(): User[] {
    return this.spaceRepo.activeSpace?.populatedUsers || [];
  }
}
